import { useCallback, useEffect, useMemo, useState } from "react";
import platformAPI from "../../../services/platformApi";
import ownerAPI from "../../../services/ownerApi";
import { Modal, useToast } from "../../ui";
import ClubSubscriptionPlanPicker from "../../common/ClubSubscription/ClubSubscriptionPlanPicker";
import ss from "../../common/ClubSubscription/subscription.module.css";

export default function OwnerClubPayModal({ club, open, onClose }) {
  const toast = useToast();
  const billing = club?.billing;
  const clubName = club?.name || "клуб";
  const isTrial = Boolean(billing?.is_trial && billing?.is_paid);

  const [plans, setPlans] = useState([]);
  const [period, setPeriod] = useState(billing?.billing_period || "monthly");
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [payingPlanId, setPayingPlanId] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const businessPlan = useMemo(
    () => plans.find((p) => p.code === "business") || null,
    [plans],
  );

  const currentPlanId = useMemo(() => {
    if (billing?.saas_plan_id) return billing.saas_plan_id;
    const byCode = plans.find((p) => p.code === billing?.plan_code);
    return byCode?.id || club?.plan?.id || null;
  }, [billing, plans, club?.plan?.id]);

  useEffect(() => {
    if (!open) return;
    platformAPI
      .getPublicPlans()
      .then((res) => setPlans(res.plans || []))
      .catch(() => setPlans([]));
  }, [open]);

  useEffect(() => {
    if (!open || !billing) return;
    if (billing.billing_period) setPeriod(billing.billing_period);
    if (currentPlanId) {
      setSelectedPlanId(currentPlanId);
      return;
    }
    if (isTrial && businessPlan) setSelectedPlanId(businessPlan.id);
  }, [open, billing, isTrial, businessPlan, currentPlanId]);

  useEffect(() => {
    if (!open || !club?.id || !selectedPlanId || !period) {
      setCheckout(null);
      return undefined;
    }
    let cancelled = false;
    setCheckoutLoading(true);
    ownerAPI
      .getClubSubscriptionCheckout(club.id, {
        saas_plan_id: selectedPlanId,
        crm_billing_period: period,
      })
      .then((res) => {
        if (!cancelled) setCheckout(res.checkout || null);
      })
      .catch(() => {
        if (!cancelled) setCheckout(null);
      })
      .finally(() => {
        if (!cancelled) setCheckoutLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, club?.id, selectedPlanId, period]);

  const payPlan = useCallback(
    async (planId) => {
      if (!club?.id || !planId) return;
      setSelectedPlanId(planId);
      setPayingPlanId(planId);
      try {
        const payData = await ownerAPI.initClubPayment(club.id, {
          saas_plan_id: planId,
          crm_billing_period: period,
        });
        if (payData.payment_url && String(payData.payment_url).startsWith("http")) {
          window.location.assign(payData.payment_url);
          return;
        }
        toast.error("Ссылка на оплату не получена. Проверьте настройки ЮKassa.");
      } catch (e) {
        toast.error(e.response?.data?.error || "Онлайн-оплата недоступна");
      } finally {
        setPayingPlanId(null);
      }
    },
    [club?.id, period, toast],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Тариф · ${clubName}`}
      size="lg"
    >
      <div className={ss.subRoot}>
        <ClubSubscriptionPlanPicker
          plans={plans}
          period={period}
          onPeriodChange={setPeriod}
          selectedPlanId={selectedPlanId}
          onSelectPlan={setSelectedPlanId}
          onPay={payPlan}
          payingPlanId={payingPlanId}
          isTrial={isTrial}
          clubName={clubName}
          canPay
          currentPlanId={currentPlanId}
          renewOnly={Boolean(billing?.renew_only)}
          currentPeriod={billing?.billing_period || "monthly"}
          hasActivePaidPeriod={Boolean(billing?.has_active_paid_period)}
          checkout={checkout}
          checkoutLoading={checkoutLoading}
        />
      </div>
    </Modal>
  );
}
